import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Save, Trash2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import { productApi } from '../../services/api';
import { useToast } from '../hooks/useToast';

const Inventory = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { success, warning, error: showError } = useToast();

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await productApi.list({ limit: 300, sort: 'stock' });
      setProducts(response.data?.data || []);
      setSelectedProductIds([]);
    } catch (error) {
      warning(error.response?.data?.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [warning]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const inventoryRows = products.flatMap((product) => {
    const productId = product._id || product.id;
    const baseRow = {
      ...product,
      id: productId,
      rowId: `${productId}-base`,
      productId,
      variantLabel: product.colorName || product.color ? `Base / ${product.colorName || product.color}` : 'Base product',
      variantId: null
    };
    const variants = (product.variants || []).map((variant) => ({
      ...product,
      id: productId,
      productId,
      rowId: `${productId}-${variant._id || variant.id}`,
      variantId: variant._id || variant.id,
      variantLabel: [variant.colorName || variant.color, variant.size].filter(Boolean).join(' / ') || variant.name || 'Variant',
      sku: variant.sku || product.sku,
      stock: variant.stock ?? 0
    }));
    return [baseRow, ...variants];
  });

  const uniqueProductIds = [...new Set(inventoryRows.map((row) => row.productId).filter(Boolean))];
  const selectedCount = selectedProductIds.length;
  const allSelected = uniqueProductIds.length > 0 && uniqueProductIds.every((id) => selectedProductIds.includes(id));

  const toggleProductSelection = (productId) => {
    setSelectedProductIds((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    );
  };

  const toggleAllSelection = () => {
    setSelectedProductIds(allSelected ? [] : uniqueProductIds);
  };

  const saveStock = async (product) => {
    const stock = Number(editing[product.rowId] ?? product.stock);
    try {
      await productApi.updateStock(product._id ?? product.id, stock, product.variantId);
      success('Stock updated');
      loadProducts();
    } catch (error) {
      warning(error.response?.data?.message || 'Could not update stock');
    }
  };

  const deleteProducts = async (ids) => {
    if (!ids.length) return;
    try {
      setDeleting(true);
      if (ids.length === 1) {
        await productApi.delete(ids[0]);
      } else {
        await productApi.bulkDelete(ids);
      }
      success(ids.length === 1 ? 'Product deleted from database' : `${ids.length} products deleted from database`);
      setDeleteTarget(null);
      setBulkDeleteOpen(false);
      setSelectedProductIds([]);
      loadProducts();
    } catch (error) {
      showError(error.response?.data?.message || 'Could not delete product');
    } finally {
      setDeleting(false);
    }
  };

  const lowStockProducts = inventoryRows.filter((product) => Number(product.stock || 0) < 10);

  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleAllSelection}
          aria-label="Select all products"
          className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
        />
      ),
      sortable: false,
      render: (_, row) => (
        <input
          type="checkbox"
          checked={selectedProductIds.includes(row.productId)}
          onChange={() => toggleProductSelection(row.productId)}
          aria-label={`Select ${row.name}`}
          className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
        />
      )
    },
    { key: 'name', label: 'Product', sortable: true },
    { key: 'variantLabel', label: 'Colour / Size', sortable: true },
    { key: 'sku', label: 'SKU', sortable: true },
    { key: 'category', label: 'Category', sortable: true },
    {
      key: 'stock',
      label: 'Current Stock',
      render: (val, row) => (
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            value={editing[row.rowId] ?? val ?? 0}
            onChange={(event) => setEditing((prev) => ({ ...prev, [row.rowId]: event.target.value }))}
            className="w-20 px-2 py-1 border border-gray-200 rounded"
          />
          <button onClick={() => saveStock(row)} className="p-1.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200" aria-label="Save stock">
            <Save size={14} />
          </button>
        </div>
      ),
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, row) => (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          Number(row.stock || 0) < 10 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
        }`}>
          {Number(row.stock || 0) < 10 ? `${Number(row.stock || 0)} left` : 'In Stock'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, row) => (
        <button
          type="button"
          onClick={() => setDeleteTarget(row)}
          className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100"
        >
          <Trash2 size={15} />
          Delete
        </button>
      )
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inventory Management"
        description="Monitor and manage product stock levels"
        breadcrumbs={['Home', 'Inventory']}
      />

      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-red-100 p-2 rounded-lg">
            <AlertTriangle className="text-red-600" size={20} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-red-900">Low Stock Alert</h3>
            <p className="text-sm text-red-700">{lowStockProducts.length} inventory item(s) below 10 units</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">
          {selectedCount > 0 ? `${selectedCount} product${selectedCount === 1 ? '' : 's'} selected for deletion` : 'Select products to bulk delete from inventory and database'}
        </p>
        <button
          type="button"
          disabled={selectedCount === 0}
          onClick={() => setBulkDeleteOpen(true)}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trash2 size={16} />
          Delete Selected
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center">
          <Loader2 className="animate-spin text-amber-600 mr-2" size={20} />
          <span className="text-gray-600">Loading inventory...</span>
        </div>
      ) : (
        <DataTable
          title="All Products"
          columns={columns}
          data={inventoryRows}
          searchable={true}
          paginate={true}
          itemsPerPage={10}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteProducts([deleteTarget?.productId].filter(Boolean))}
        title="Delete Product"
        message={`Delete "${deleteTarget?.name || 'this product'}" from inventory and database?`}
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        isDangerous={true}
      />

      <ConfirmDialog
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => deleteProducts(selectedProductIds)}
        title="Bulk Delete Products"
        message={`Delete ${selectedCount} selected product${selectedCount === 1 ? '' : 's'} from inventory and database?`}
        confirmText={deleting ? 'Deleting...' : 'Delete Selected'}
        isDangerous={true}
      />
    </div>
  );
};

export default Inventory;
